import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useAccessibilityPreferences } from "providers/AccessibilityPreferencesProvider";
import {
    STREAM_PATH_OVERLAY_SLIDER_LIMITS,
    type StreamPathOverlayPreferences,
} from "lib/consts/streamPathOverlay";

const PRESET_COLOR_KEYS = [
    { hex: "#00e096", labelKey: "accessibility.presetColors.defaultGreen" },
    { hex: "#07BFA7", labelKey: "accessibility.presetColors.teal" },
    { hex: "#FDE047", labelKey: "accessibility.presetColors.yellow" },
    { hex: "#FB923C", labelKey: "accessibility.presetColors.orange" },
    { hex: "#F87171", labelKey: "accessibility.presetColors.red" },
    { hex: "#60A5FA", labelKey: "accessibility.presetColors.blue" },
    { hex: "#C084FC", labelKey: "accessibility.presetColors.purple" },
    { hex: "#FFFFFF", labelKey: "accessibility.presetColors.white" },
] as const;

const PATH_COLOR_PRESET_KEYS = [
    { hex: "#3b82f6", labelKey: "accessibility.pathColorPresets.defaultBlue" },
    { hex: "#22c55e", labelKey: "accessibility.pathColorPresets.green" },
    { hex: "#FDE047", labelKey: "accessibility.pathColorPresets.yellow" },
    { hex: "#FB923C", labelKey: "accessibility.pathColorPresets.orange" },
    { hex: "#F87171", labelKey: "accessibility.pathColorPresets.red" },
    { hex: "#FFFFFF", labelKey: "accessibility.pathColorPresets.white" },
] as const;

const RANGE_CLASS =
    "h-4 w-full min-w-[6rem] flex-1 cursor-pointer bg-transparent " +
    "appearance-none accent-primary2 outline-none " +
    "[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full " +
    "[&::-webkit-slider-runnable-track]:bg-black70 " +
    "[&::-webkit-slider-thumb]:-mt-0.5 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 " +
    "[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none " +
    "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 " +
    "[&::-webkit-slider-thumb]:bg-primary2 " +
    "[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-black70 " +
    "[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer " +
    "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary2";

type SliderRowProps = {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    format: (value: number) => string;
    onChange: (value: number) => void;
    ariaLabel: string;
};

const SliderRow: React.FC<SliderRowProps> = ({
    label,
    value,
    min,
    max,
    step,
    format,
    onChange,
    ariaLabel,
}) => (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <span className="w-full shrink-0 text-h5 text-black40 sm:w-44">{label}</span>
        <div className="flex min-w-0 flex-1 items-center gap-3">
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => {
                    const next = Number.parseFloat(e.target.value);
                    if (Number.isFinite(next)) {
                        onChange(next);
                    }
                }}
                className={RANGE_CLASS}
                aria-label={ariaLabel}
            />
            <span className="w-16 shrink-0 text-right font-mono text-h5 tabular-nums text-white">
                {format(value)}
            </span>
        </div>
    </div>
);

const AccessibilitySettingsPanel: FC = () => {
    const { t } = useTranslation("settings");
    const {
        colorMode,
        setColorMode,
        bboxOverlayColor,
        setBboxOverlayColor,
        streamPathOverlay,
        setStreamPathOverlayField,
        resetStreamPathOverlay,
    } = useAccessibilityPreferences();

    const setPathField = <K extends keyof StreamPathOverlayPreferences>(
        key: K,
        value: StreamPathOverlayPreferences[K],
    ) => setStreamPathOverlayField(key, value);

    const colorModeOptions = [
        { value: "light" as const, label: t("accessibility.colorModeLight") },
        { value: "dark" as const, label: t("accessibility.colorModeDark") },
        { value: "system" as const, label: t("accessibility.colorModeSystem") },
    ];

    return (
        <div className="flex max-w-3xl flex-col gap-6">
            <div>
                <p className="text-h4 font-semibold text-white">{t("accessibility.title")}</p>
                <p className="mt-1 text-h5 text-black40">
                    {t("accessibility.bboxDescription")}
                </p>
            </div>

            <div className="rounded-r10 border border-black80 bg-black90 p-4">
                <p className="text-h5 font-semibold text-white">
                    {t("accessibility.appearanceTitle")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                    {t("accessibility.appearanceDescription")}
                </p>
                <div
                    className="mt-4 flex flex-wrap gap-2"
                    role="radiogroup"
                    aria-label={t("accessibility.appearanceTitle")}
                >
                    {colorModeOptions.map(({ value, label }) => {
                        const selected = colorMode === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => setColorMode(value)}
                                className={`rounded-r5 border px-4 py-2 text-h5 transition-colors ${
                                    selected
                                        ? "border-primary2 bg-primary2 text-on-accent"
                                        : "border-black80 bg-black70 text-white hover:bg-black60"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="rounded-r10 border border-black80 bg-black90 p-4">
                <p className="text-h5 font-semibold text-white">
                    {t("accessibility.bboxColour")}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                    <label className="flex cursor-pointer items-center gap-3">
                        <span className="text-h5 text-black40">{t("accessibility.custom")}</span>
                        <input
                            type="color"
                            value={bboxOverlayColor}
                            onChange={(e) => setBboxOverlayColor(e.target.value)}
                            className="h-10 w-14 cursor-pointer rounded-r3 border border-black80 bg-black70 p-0.5"
                            aria-label={t("accessibility.pickBboxColour")}
                        />
                    </label>
                    <span className="font-mono text-h5 text-white tabular-nums">
                        {bboxOverlayColor.toUpperCase()}
                    </span>
                </div>

                <p className="mt-4 text-h5 text-black40">{t("accessibility.presets")}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {PRESET_COLOR_KEYS.map(({ hex, labelKey }) => {
                        const label = t(labelKey);
                        return (
                            <button
                                key={hex}
                                type="button"
                                title={label}
                                aria-label={t("accessibility.ariaLabels.presetColour", { label, hex })}
                                onClick={() => setBboxOverlayColor(hex)}
                                className={`h-9 w-9 shrink-0 rounded-r5 border-2 transition-transform hover:scale-105 ${
                                    bboxOverlayColor.toLowerCase() === hex.toLowerCase()
                                        ? "border-primary2 ring-2 ring-primary2/50"
                                        : "border-black80"
                                }`}
                                style={{ backgroundColor: hex }}
                            />
                        );
                    })}
                </div>
            </div>

            <div className="rounded-r10 border border-black80 bg-black90 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-h5 font-semibold text-white">
                            {t("accessibility.pathTitle")}
                        </p>
                        <p className="mt-1 text-h5 text-black40">
                            {t("accessibility.pathDescription")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={resetStreamPathOverlay}
                        className="shrink-0 rounded-r5 border border-black80 bg-black70 px-3 py-1.5 text-h5 text-white hover:bg-black60"
                    >
                        {t("accessibility.resetDefaults")}
                    </button>
                </div>

                <div className="mt-5 space-y-4">
                    <SliderRow
                        label={t("accessibility.horizontalFov")}
                        value={streamPathOverlay.hfovDeg}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.hfovDeg}
                        format={(v) => t("accessibility.degrees", { value: v.toFixed(0) })}
                        onChange={(v) => setPathField("hfovDeg", v)}
                        ariaLabel={t("accessibility.ariaLabels.horizontalFov")}
                    />
                    <SliderRow
                        label={t("accessibility.verticalFov")}
                        value={streamPathOverlay.vfovDeg}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.vfovDeg}
                        format={(v) => t("accessibility.degrees", { value: v.toFixed(0) })}
                        onChange={(v) => setPathField("vfovDeg", v)}
                        ariaLabel={t("accessibility.ariaLabels.verticalFov")}
                    />
                    <SliderRow
                        label={t("accessibility.cameraPitch")}
                        value={streamPathOverlay.pitchDeg}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.pitchDeg}
                        format={(v) => t("accessibility.degrees", { value: v.toFixed(1) })}
                        onChange={(v) => setPathField("pitchDeg", v)}
                        ariaLabel={t("accessibility.ariaLabels.cameraPitch")}
                    />
                    <SliderRow
                        label={t("accessibility.cameraHeight")}
                        value={streamPathOverlay.heightM}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.heightM}
                        format={(v) => t("accessibility.meters", { value: v.toFixed(2) })}
                        onChange={(v) => setPathField("heightM", v)}
                        ariaLabel={t("accessibility.ariaLabels.cameraHeight")}
                    />
                    <SliderRow
                        label={t("accessibility.forwardOffset")}
                        value={streamPathOverlay.forwardOffsetM}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.forwardOffsetM}
                        format={(v) => t("accessibility.meters", { value: v.toFixed(2) })}
                        onChange={(v) => setPathField("forwardOffsetM", v)}
                        ariaLabel={t("accessibility.ariaLabels.forwardOffset")}
                    />
                    <SliderRow
                        label={t("accessibility.lineWidth")}
                        value={streamPathOverlay.lineWidth}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.lineWidth}
                        format={(v) => t("accessibility.pixels", { value: v.toFixed(1) })}
                        onChange={(v) => setPathField("lineWidth", v)}
                        ariaLabel={t("accessibility.ariaLabels.lineWidth")}
                    />
                    <SliderRow
                        label={t("accessibility.opacity")}
                        value={streamPathOverlay.opacity}
                        {...STREAM_PATH_OVERLAY_SLIDER_LIMITS.opacity}
                        format={(v) => v.toFixed(2)}
                        onChange={(v) => setPathField("opacity", v)}
                        ariaLabel={t("accessibility.ariaLabels.opacity")}
                    />
                </div>

                <p className="mt-5 text-h5 text-black40">{t("accessibility.pathColour")}</p>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                    <label className="flex cursor-pointer items-center gap-3">
                        <span className="text-h5 text-black40">{t("accessibility.custom")}</span>
                        <input
                            type="color"
                            value={streamPathOverlay.pathColor}
                            onChange={(e) => setPathField("pathColor", e.target.value)}
                            className="h-10 w-14 cursor-pointer rounded-r3 border border-black80 bg-black70 p-0.5"
                            aria-label={t("accessibility.pickPathColour")}
                        />
                    </label>
                    <span className="font-mono text-h5 text-white tabular-nums">
                        {streamPathOverlay.pathColor.toUpperCase()}
                    </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    {PATH_COLOR_PRESET_KEYS.map(({ hex, labelKey }) => {
                        const label = t(labelKey);
                        return (
                            <button
                                key={hex}
                                type="button"
                                title={label}
                                aria-label={t("accessibility.ariaLabels.presetColour", { label, hex })}
                                onClick={() => setPathField("pathColor", hex)}
                                className={`h-9 w-9 shrink-0 rounded-r5 border-2 transition-transform hover:scale-105 ${
                                    streamPathOverlay.pathColor.toLowerCase() === hex.toLowerCase()
                                        ? "border-primary2 ring-2 ring-primary2/50"
                                        : "border-black80"
                                }`}
                                style={{ backgroundColor: hex }}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default AccessibilitySettingsPanel;
